import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import { GetById } from "adminApi/EmployeeSalaryPaymentApi/EmployeeSalaryPayment";
import moment from "moment";
import html2pdf from "html2pdf.js";
import EmployeeSalaryInvoicePrint from "./EmployeeSalaryInvoicePrint";

const EmployeeSalaryPaymentView = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const printRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await GetById(id);
                if (response?.data?.success && response?.data?.data) {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching payment details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id]);

    if (loading) {
        return (
            <PageBody card={true} headerchildren={<HeadingOne>Employee Salary Payment Details</HeadingOne>}>
                <div className="d-flex justify-content-center align-items-center w-100 p-5">
                    Data Loading...
                </div>
            </PageBody>
        );
    }

    if (!data) {
        return (
            <PageBody card={true} headerchildren={<HeadingOne>Employee Salary Payment Details</HeadingOne>}>
                <div className="text-center p-5 text-muted">
                    No Details Found
                </div>
            </PageBody>
        );
    }

    const getStatusVariant = (status) => {
        if (!status) return "secondary";
        const lowered = status.toLowerCase();
        if (lowered === 'paid') return "success";
        if (lowered === 'cancelled') return "danger";
        return "warning";
    };

    // Calculate dynamically
    const basicSalary = Number(data.basic_salary || 0);
    const allowancesItems = (data.allowances_list || []).filter(item => item.name.toLowerCase() !== 'basic' && item.name.toLowerCase() !== 'basic salary');
    const allowancesSum = allowancesItems.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const grossSalary = data.allowances_list && data.allowances_list.length > 0 ? basicSalary + allowancesSum : Number(data.gross_salary || 0);

    const deductionsItems = data.deductions_list || [];
    const deductionsSum = deductionsItems.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const leaveDeductions = Number(data.total_deductions || 0);
    const totalDeductions = deductionsSum + leaveDeductions;
    const netSalary = grossSalary - totalDeductions;

    const handleNativePrint = () => {
        const printContents = printRef.current.innerHTML;
        const printWindow = window.open("", "_blank", "width=800,height=600");

        printWindow.document.write(`
          <html>
            <head>
              <title>Print Salary Slip</title>
              <!-- Core minimal styles for printing if required, else native is cleanly formatted via inline styles on the element -->
            </head>
            <body style="margin: 0;">
              ${printContents}
            </body>
          </html>
        `);

        printWindow.document.close();

        // Needs minor delay for complete render before print trigger, common with injected HTML strings
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 100);
    };

    const handleDownloadPdf = () => {
        const element = printRef.current;
        const opt = {
            margin: 10,
            filename: `SalarySlip_${data.employee_name.replace(/\s+/g, '_')}_${moment(data.end_date).format('MMM_YYYY')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().from(element).set(opt).save();
    };

    return (
        <>
            {/* Hidden Print Component rendering offline */}
            <div style={{
                height: "0px",
                overflow: "hidden",
                position: "absolute",
                zIndex: -1
            }}>
                <EmployeeSalaryInvoicePrint ref={printRef} data={data} />
            </div>

            <PageBody
                card={true}
                headerchildren={
                    <>
                        <HeadingOne>Employee Salary Payment Details</HeadingOne>
                        <div className="d-flex align-items-center gap-2">
                            <button className="btn btn-outline-primary ms-auto" onClick={handleNativePrint}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-printer me-2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                                Print
                            </button>
                            <button className="btn btn-primary" onClick={handleDownloadPdf}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-download me-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                Download PDF
                            </button>
                        </div>
                    </>
                }
            >
                {/* Employee & Base Info */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                    <h5 className="border-bottom pb-2 mb-3 text-primary">Employee Information</h5>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0">
                            <tbody>
                                <tr><th className="bg-light w-50">Employee Name</th><td>{data.employee_name || "N/A"}</td></tr>
                                <tr><th className="bg-light">Email</th><td>{data.employee_email || "N/A"}</td></tr>
                                <tr><th className="bg-light">Phone</th><td>{data.employee_phone || "N/A"}</td></tr>
                                <tr><th className="bg-light">Department</th><td>{data.department_text || "N/A"}</td></tr>
                                <tr><th className="bg-light">Rate Type</th><td className="text-capitalize">{data.rate_type?.toLowerCase() || "N/A"}</td></tr>
                                <tr><th className="bg-light">Pay Frequency</th><td className="text-capitalize">{data.pay_frequency?.toLowerCase() || "N/A"}</td></tr>
                                <tr><th className="bg-light">Working Days</th><td>{data.working_days || 0} days</td></tr>
                                <tr><th className="bg-light">Period</th><td>{data.start_date ? moment(data.start_date).format("MMM DD, YYYY") : "N/A"} - {data.end_date ? moment(data.end_date).format("MMM DD, YYYY") : "N/A"}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Payment Status & Details */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                    <h5 className="border-bottom pb-2 mb-3 text-primary">Payment Details</h5>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0">
                            <tbody>
                                <tr><th className="bg-light w-50">Status</th><td><span className={`badge bg-${getStatusVariant(data.payment_status)} text-uppercase`}>{data.payment_status || "Pending"}</span></td></tr>
                                <tr><th className="bg-light">Due Date</th><td>{data.payment_due_date ? moment(data.payment_due_date).format("MMM DD, YYYY") : "N/A"}</td></tr>
                                <tr><th className="bg-light">Payment Date</th><td>{data.payment_date ? moment(data.payment_date).format("MMM DD, YYYY") : "Not Paid Yet"}</td></tr>
                                <tr><th className="bg-light">Paid By</th><td>{data.paid_by || "N/A"} {data.payment_method ? `(${data.payment_method.replace("_", " ")})` : ""}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Salary Summary List */}
                <div className="col-12 mt-4">
                    <h5 className="border-bottom pb-2 mb-3 text-primary">Salary Breakdown</h5>
                    <div className="row gy-4">
                        {/* Earnings BreakDown */}
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <h6 className="text-success mb-2 fw-bold">Earnings</h6>
                            <div className="table-responsive">
                                <table className="table table-bordered mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Description</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Basic Salary</td>
                                            <td className="text-end">${basicSalary.toFixed(2)}</td>
                                        </tr>
                                        {allowancesItems.length > 0 ? (
                                            allowancesItems.map((item, idx) => (
                                                <tr key={`al_${idx}`}>
                                                    <td>{item.name} {Number(item.percentage) > 0 ? `(${item.percentage}%)` : ''}</td>
                                                    <td className="text-end">${Number(item.amount || 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            data.allowances > 0 && (
                                                <tr>
                                                    <td>Allowances</td>
                                                    <td className="text-end">${Number(data.allowances || 0).toFixed(2)}</td>
                                                </tr>
                                            )
                                        )}
                                        <tr className="bg-light">
                                            <td className="fw-bold">Gross Salary</td>
                                            <td className="text-end fw-bold text-success">${grossSalary.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Deductions BreakDown */}
                        <div className="col-lg-6 col-md-6 col-sm-12">
                            <h6 className="text-danger mb-2 fw-bold">Deductions</h6>
                            <div className="table-responsive">
                                <table className="table table-bordered mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Description</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deductionsItems.length > 0 && (
                                            deductionsItems.map((item, idx) => (
                                                <tr key={`de_${idx}`}>
                                                    <td>{item.name} {Number(item.percentage) > 0 ? `(${item.percentage}%)` : ''}</td>
                                                    <td className="text-end">-${Number(item.amount || 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        )}

                                        <tr>
                                            <td>Leave Deductions
                                                <div className="text-muted small">
                                                    (Paid: {data.paid_leave_days || 0}, Unpaid: {data.unpaid_leave_days || 0})
                                                </div>
                                            </td>
                                            <td className="text-end">-${leaveDeductions.toFixed(2)}</td>
                                        </tr>

                                        <tr className="bg-light">
                                            <td className="fw-bold">Total Deductions</td>
                                            <td className="text-end fw-bold text-danger">-${totalDeductions.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Net Salary Block */}
                    <div className="table-responsive mt-4">
                        <table className="table table-bordered mb-0">
                            <tbody>
                                <tr className="bg-light h5">
                                    <th className="py-3">Net Salary Payable</th>
                                    <th className="text-end text-success py-3">${netSalary.toFixed(2)}</th>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Notes */}
                {data.notes && (
                    <div className="col-12">
                        <div className="bg-light p-3 rounded border">
                            <span className="fw-bold d-block mb-1">Additional Notes:</span>
                            <span className="text-muted">{data.notes}</span>
                        </div>
                    </div>
                )}
            </PageBody>
        </>
    );
};

export default EmployeeSalaryPaymentView;
